import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextField, MenuItem, Button } from '@material-ui/core';
import TranslateIcon from '@material-ui/icons/Translate';
import CloseIcon from '@material-ui/icons/Close';
import { tr } from '@/common/utils/tr';
import trI18n from '@/common/utils/trI18n';
import MuiTooltip from '@/component/common/MuiTooltip';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import { isOverTranslationMaxLength } from '@/common/utils/translationUtils';
import TranslationManagementConstant from '../TranslationManagementConstant';

const { NEW_ROW_ERROR, PIVOT_LANGUAGE } = TranslationManagementConstant;

// 「翻訳先言語を追加」 dropdown: opens under the input, long language list scrolls (same as the toolbar selects).
const ADD_LANGUAGE_SELECT_PROPS = {
    displayEmpty: true,
    // Always shows the placeholder: the dropdown resets after each pick (CR-303142 BR-3).
    renderValue: () => <span className="tm-placeholder">{tr('翻訳先言語を追加')}</span>,
    MenuProps: {
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
        getContentAnchorEl: null,
        className: 'selectDrop tm-select-menu'
    }
};

/**
 * Client-side mirror of the server validation (VR-2 / VR-4 / VR-6), so the admin
 * sees the error without a round-trip. The server validates again (and alone
 * checks duplicates - VR-1).
 *
 * @returns {{ keywordError: string, translationError: string }} NEW_ROW_ERROR codes, '' when valid.
 */
export const validateDraft = (keywordLanguage, keywordText, translations) => {
    const keyword = keywordText.trim();
    const texts = Object.values(translations).map(text => (text || '').trim());

    if (!keyword) return { keywordError: NEW_ROW_ERROR.REQUIRED, translationError: '' };
    // Each length error is shown under the field it belongs to.
    if (isOverTranslationMaxLength(keyword)) return { keywordError: NEW_ROW_ERROR.MAX_LENGTH, translationError: '' };
    if (texts.some(isOverTranslationMaxLength)) return { keywordError: '', translationError: NEW_ROW_ERROR.MAX_LENGTH };
    if (keywordLanguage === PIVOT_LANGUAGE) {
        return { keywordError: '', translationError: texts.some(Boolean) ? '' : NEW_ROW_ERROR.TRANSLATION_REQUIRED };
    }
    const pivot = (translations[PIVOT_LANGUAGE] || '').trim();
    return { keywordError: '', translationError: pivot ? '' : NEW_ROW_ERROR.PIVOT_REQUIRED };
};

/**
 * Draft Card: creates one Terminology entry (keyword + its translations) in ONE step.
 *
 * Pinned at the top of the grid. 保存 / Enter validates and saves the whole entry,
 * キャンセル / Esc discards it. The translation rows list every org language except the
 * keyword language; Japanese is always listed because it is the glossary pivot. The admin
 * may add any other supported language through「翻訳先言語を追加」(CR-303142); only those
 * hand-added rows can be removed again.
 * Language codes and typed texts are data - never wrapped with tr().
 */
export default function TerminologyDraftCard({
    orgLanguages,
    languageOptions,
    defaultSourceLanguage,
    submitting,
    serverError,
    renderError,
    onSubmit,
    onCancel,
    onPreviewTranslate
}) {
    useTranslationSubscription();

    const [keywordLanguage, setKeywordLanguage] = useState(defaultSourceLanguage);
    const [keywordText, setKeywordText] = useState('');
    const [translations, setTranslations] = useState({});
    // Languages the admin added through「翻訳先言語を追加」, in the order they were added.
    const [addedLanguages, setAddedLanguages] = useState([]);
    // コンテンツ翻訳に利用 - ON by default (spec §6.5), the admin may switch it OFF.
    const [useAsTerminology, setUseAsTerminology] = useState(true);
    const [errors, setErrors] = useState({ keywordError: '', translationError: '' });
    const [translatingLanguage, setTranslatingLanguage] = useState('');
    const cardRef = useRef(null);

    // Auto-scroll to the pinned card when it opens (the keyword input has autoFocus).
    useEffect(() => {
        if (cardRef.current?.scrollIntoView) cardRef.current.scrollIntoView({ block: 'nearest' });
    }, []);

    // A server error (e.g. duplicate) is shown under the field it belongs to.
    useEffect(() => {
        if (!serverError) return;
        const isTranslationError = [NEW_ROW_ERROR.PIVOT_REQUIRED, NEW_ROW_ERROR.TRANSLATION_REQUIRED].includes(serverError);
        setErrors(
            isTranslationError
                ? { keywordError: '', translationError: serverError }
                : { keywordError: serverError, translationError: '' }
        );
    }, [serverError]);

    // Rows that are always listed (org languages + the pivot) - they have no remove button.
    const baseLanguages = useMemo(
        () => [...new Set([PIVOT_LANGUAGE, ...orgLanguages])].filter(language => language !== keywordLanguage).sort(),
        [orgLanguages, keywordLanguage]
    );

    // Base rows first, then the hand-added rows (never the keyword language, never twice).
    const targetLanguages = useMemo(
        () => [...baseLanguages, ...addedLanguages.filter(language => language !== keywordLanguage && !baseLanguages.includes(language))],
        [baseLanguages, addedLanguages, keywordLanguage]
    );

    // Options of「翻訳先言語を追加」: supported languages without a row yet, minus the keyword language.
    const addableLanguages = useMemo(
        () => languageOptions.filter(language => language !== keywordLanguage && !targetLanguages.includes(language)),
        [languageOptions, keywordLanguage, targetLanguages]
    );

    const clearErrors = () => setErrors({ keywordError: '', translationError: '' });

    /** Keyword language changed: the row of that language leaves the card with its text (BR-7). */
    const handleChangeKeywordLanguage = language => {
        setKeywordLanguage(language);
        setAddedLanguages(prev => prev.filter(added => added !== language));
        setTranslations(prev => {
            const next = { ...prev };
            delete next[language];
            return next;
        });
        clearErrors();
    };

    const handleAddLanguage = language => {
        if (!language) return;
        setAddedLanguages(prev => (prev.includes(language) ? prev : [...prev, language]));
    };

    const handleRemoveLanguage = language => {
        setAddedLanguages(prev => prev.filter(added => added !== language));
        setTranslations(prev => {
            const next = { ...prev };
            delete next[language];
            return next;
        });
        clearErrors();
    };

    const handleSubmit = () => {
        if (submitting) return;
        // Only the languages still listed: a text typed for the language that became the keyword language is dropped.
        // Rows left empty (base or hand-added) are dropped too; validateDraft only counts filled rows (BR-8).
        const visible = {};
        targetLanguages.forEach(language => {
            visible[language] = translations[language] || '';
        });
        const validation = validateDraft(keywordLanguage, keywordText, visible);
        if (validation.keywordError || validation.translationError) {
            setErrors(validation);
            return;
        }
        onSubmit({
            sourceLanguage: keywordLanguage,
            sourceText: keywordText.trim(),
            useAsTerminology,
            translations: targetLanguages
                .map(language => ({ targetLanguage: language, translatedText: (visible[language] || '').trim() }))
                .filter(translation => translation.translatedText)
        });
    };

    const handleKeyDown = e => {
        // Enter saves, Esc discards - but never while a dropdown menu (handled by MUI) is focused.
        if (e.key === 'Enter' && !e.shiftKey && e.target.tagName === 'INPUT') {
            e.preventDefault();
            handleSubmit();
        } else if (e.key === 'Escape' && !submitting) {
            e.preventDefault();
            onCancel();
        }
    };

    const handleTranslate = async targetLanguage => {
        if (!keywordText.trim()) {
            setErrors({ keywordError: NEW_ROW_ERROR.REQUIRED, translationError: '' });
            return;
        }
        setTranslatingLanguage(targetLanguage);
        try {
            const suggestion = await onPreviewTranslate(keywordText.trim(), keywordLanguage, targetLanguage);
            if (suggestion) {
                setTranslations(prev => ({ ...prev, [targetLanguage]: suggestion }));
                clearErrors();
            }
        } finally {
            setTranslatingLanguage('');
        }
    };

    const isBusy = submitting || Boolean(translatingLanguage);
    // The suggestion translates the keyword, so it needs one (BR-6).
    const hasKeyword = Boolean(keywordText.trim());
    const isAllLanguagesAdded = addableLanguages.length === 0;

    return (
        <tr className="tm-new-row tm-draft-row" ref={cardRef}>
            <td colSpan={7}>
                <div className="tm-draft-card" onKeyDown={handleKeyDown}>
                    <div className="tm-draft-parent">
                        <span className="tm-badge tm-badge-term">{tr('用語')}</span>
                        <TextField
                            select
                            className="tm-select tm-draft-language form-control form-control--wrap form-control--hg40"
                            variant="outlined"
                            inputProps={{ 'aria-label': tr('翻訳元言語') }}
                            value={keywordLanguage}
                            onChange={e => handleChangeKeywordLanguage(e.target.value)}
                            disabled={isBusy}
                        >
                            {languageOptions.map(code => (
                                <MenuItem key={code} value={code}>
                                    {code}
                                </MenuItem>
                            ))}
                        </TextField>
                        <div className="tm-draft-keyword">
                            <TextField
                                className="tm-text-input"
                                variant="outlined"
                                size="small"
                                fullWidth
                                autoFocus
                                placeholder={tr('キーワードを入力してください')}
                                value={keywordText}
                                onChange={e => {
                                    setKeywordText(e.target.value);
                                    clearErrors();
                                }}
                                disabled={submitting}
                                error={Boolean(errors.keywordError)}
                            />
                            {errors.keywordError && <div className="tm-new-row-error">{renderError(errors.keywordError)}</div>}
                        </div>
                        <MuiTooltip title={tr('コンテンツ翻訳に利用')}>
                            <span className="tm-term-cell">
                                <span className="tm-switch">
                                    <input
                                        id="tm-draft-term"
                                        type="checkbox"
                                        checked={useAsTerminology}
                                        disabled={isBusy}
                                        onChange={e => setUseAsTerminology(e.target.checked)}
                                    />
                                    <label htmlFor="tm-draft-term" />
                                </span>
                            </span>
                        </MuiTooltip>
                    </div>

                    <table className="tm-draft-translations">
                        <thead>
                            <tr>
                                <th style={{ width: 110 }}>{tr('翻訳先言語')}</th>
                                <th>{tr('翻訳')}</th>
                                <th style={{ width: 76 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {targetLanguages.map(language => (
                                <tr key={language}>
                                    <td className="tm-lang">
                                        {language}
                                        {language === PIVOT_LANGUAGE && <span className="tm-required-mark">*</span>}
                                    </td>
                                    <td>
                                        <TextField
                                            className="tm-text-input"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            inputProps={{ 'aria-label': `${tr('翻訳')} ${language}` }}
                                            value={translations[language] || ''}
                                            onChange={e => {
                                                const { value } = e.target;
                                                setTranslations(prev => ({ ...prev, [language]: value }));
                                                clearErrors();
                                            }}
                                            disabled={submitting || translatingLanguage === language}
                                            error={
                                                errors.translationError === NEW_ROW_ERROR.MAX_LENGTH &&
                                                isOverTranslationMaxLength((translations[language] || '').trim())
                                            }
                                        />
                                    </td>
                                    <td className="tm-row-actions">
                                        <MuiTooltip title={tr('翻訳')}>
                                            <span className="tm-tooltip-wrap">
                                                <button
                                                    type="button"
                                                    className="tm-icon-btn"
                                                    onClick={() => handleTranslate(language)}
                                                    disabled={isBusy || !hasKeyword}
                                                >
                                                    <TranslateIcon style={{ fontSize: 16 }} />
                                                </button>
                                            </span>
                                        </MuiTooltip>
                                        {!baseLanguages.includes(language) && (
                                            <MuiTooltip title={tr('削除')}>
                                                <span className="tm-tooltip-wrap">
                                                    <button
                                                        type="button"
                                                        className="tm-icon-btn tm-icon-danger"
                                                        aria-label={`${tr('削除')} ${language}`}
                                                        onClick={() => handleRemoveLanguage(language)}
                                                        disabled={isBusy}
                                                    >
                                                        <CloseIcon style={{ fontSize: 16 }} />
                                                    </button>
                                                </span>
                                            </MuiTooltip>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {errors.translationError && (
                        <div className="tm-new-row-error tm-draft-translation-error">{renderError(errors.translationError)}</div>
                    )}
                    <div className="tm-draft-add-language">
                        <MuiTooltip title={isAllLanguagesAdded ? tr('すべての言語が追加されました') : ''}>
                            {/* span keeps the tooltip working while the select is disabled */}
                            <span className="tm-tooltip-wrap">
                                <TextField
                                    select
                                    className="tm-select form-control form-control--wrap form-control--hg40"
                                    variant="outlined"
                                    SelectProps={ADD_LANGUAGE_SELECT_PROPS}
                                    inputProps={{ 'aria-label': tr('翻訳先言語を追加') }}
                                    value=""
                                    onChange={e => handleAddLanguage(e.target.value)}
                                    disabled={isBusy || isAllLanguagesAdded}
                                >
                                    {/* Language code only, same as the 翻訳元言語 select. */}
                                    {addableLanguages.map(code => (
                                        <MenuItem key={code} value={code}>
                                            {code}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </span>
                        </MuiTooltip>
                    </div>

                    <div className="tm-draft-actions">
                        <Button
                            className="btn-hg32 btn-light"
                            color="primary"
                            variant="contained"
                            onClick={onCancel}
                            disabled={submitting}
                        >
                            {trI18n('CM-BUTTON-CANCEL')}
                        </Button>
                        <Button
                            className="btn-hg32 btn-primary"
                            color="primary"
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isBusy}
                        >
                            {trI18n('CM-BUTTON-SAVE')}
                        </Button>
                    </div>
                </div>
            </td>
        </tr>
    );
}

TerminologyDraftCard.propTypes = {
    orgLanguages: PropTypes.arrayOf(PropTypes.string),
    languageOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
    defaultSourceLanguage: PropTypes.string.isRequired,
    submitting: PropTypes.bool,
    serverError: PropTypes.string,
    renderError: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onPreviewTranslate: PropTypes.func.isRequired
};

TerminologyDraftCard.defaultProps = {
    orgLanguages: [],
    submitting: false,
    serverError: ''
};
